import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import { RouteComponentProps, useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getDateString } from "utils/utils";
import {
  ApplicationDto,
  ApplicationResolution,
  ApplicationStatus,
} from "model/Application";
import { CustomLink, H3, Text } from "components/Typography/text.styled";
import { useAuthStore } from "stores/authStore/authStore";
import { FontSize } from "config/font";
import { Color } from "config/colors";
import { LoaderProgress } from "components/icon/loaderProgress.component";
import { useGetGig } from "hooks/useGetGig";
import { Api } from "services/api/api.service";
import {
  ContractModalBody,
  GigApplicantsListStyled,
  HeaderContainer,
  NoApplicantsContainer,
} from "web-apps/company/components/gig/gigApplicants/gigApplicants.styled";
import { GigContract } from "web-apps/company/components/gig/gigApplicants/gigContract.component";
import { GigApplicantsListItem } from "web-apps/company/components/gig/gigApplicants/gigApplicantsListItem.component";

type MatchProps = {
  id?: string;
};

export const GigApplicantsPage: React.FC<RouteComponentProps<MatchProps>> = (
  props,
) => {
  const id = props.match.params.id;
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [applicationState] = useApplicationStore();
  const [authState] = useAuthStore();
  const [applicants, setApplicants] = useState<ApplicationDto[]>([]);
  const [activeApplicationId, setActiveApplicationId] = useState<number | null>(
    null,
  );
  const history = useHistory();
  const [gigData, isLoading, , fetch] = useGetGig(id);
  const [appIsLoading, setAppIsLoading] = useState(false);

  useEffect(() => {
    if (!companyState.company?.id || !id) return;

    fetch(id);
    setAppIsLoading(true);
    Api()
      .company.application.getGigApplications(
        companyState.company.id,
        parseInt(id, 10),
      )
      .then((res) => {
        setApplicants(res.data);
        setAppIsLoading(false);
      })
      .catch(() => {
        setAppIsLoading(false);
      });
  }, [id, companyState.company?.id, fetch]);

  useEffect(() => {
    if (
      companyState.company?.id &&
      gigData &&
      gigData.companyId !== companyState.company.id
    ) {
      history.goBack();
    }
  }, [gigData, companyState.company?.id, history]);

  return (
    <>
      {authState.isLoading ||
      companyState.isLoading ||
      isLoading ||
      appIsLoading ||
      applicationState.isLoading ? (
        <LoaderProgress />
      ) : (
        <>
          {gigData && (
            <>
              <HeaderContainer>
                <H3>
                  {t(gigData.role, { ns: "roles" })}{" "}
                  {getDateString(gigData.shifts[0].startTime)}
                </H3>
                <Text fontSize={FontSize.Large}>
                  {gigData.applications
                    ? gigData.applications.filter(
                        (application) =>
                          application.status === ApplicationStatus.CLOSED &&
                          application.resolution ===
                            ApplicationResolution.ACCEPTED,
                      ).length
                    : 0}{" "}
                  / {gigData.amountOfWorkers} {t("General.Giggers")}
                </Text>
              </HeaderContainer>
              <HeaderContainer>
                <CustomLink
                  onClick={() => history.goBack()}
                  color={Color.Destructive}
                  text-align="right"
                >
                  {t("General.Back")}
                </CustomLink>
              </HeaderContainer>
            </>
          )}

          {applicants.length > 0 ? (
            <GigApplicantsListStyled>
              {applicants.map((applicant) => (
                <GigApplicantsListItem
                  key={applicant.id}
                  applicantData={applicant}
                  handleShowContract={(application: ApplicationDto) => {
                    setActiveApplicationId(application.id);
                  }}
                  setApplications={setApplicants}
                  onWithdraw={(applicationId: number) => {
                    setApplicants((previousApplicants) =>
                      previousApplicants.filter(
                        (candidate) => candidate.id !== applicationId,
                      ),
                    );
                  }}
                />
              ))}
            </GigApplicantsListStyled>
          ) : (
            <NoApplicantsContainer>
              <Text>{t("GigsCompany.NoApplicants")}</Text>
            </NoApplicantsContainer>
          )}

          <Modal
            open={activeApplicationId !== null}
            onClose={() => setActiveApplicationId(null)}
            aria-labelledby="gig-contract-modal-title"
            aria-describedby="gig-contract-modal-description"
          >
            <ContractModalBody>
              <GigContract
                applicationId={activeApplicationId}
                closeContract={() => setActiveApplicationId(null)}
              />
            </ContractModalBody>
          </Modal>
        </>
      )}
    </>
  );
};
